import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { GenerateUrlRequestBody, Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminPatrolAnchorMapping,
  InsertAdminPatrolAnchorMappingBody,
  UpdateAdminPatrolAnchorMappingBody,
} from "./types/patrolAnchorMappings";

export const usePatrolAnchorMappingsApi = () => {
  const api = useApi();

  const getAdminPatrolAnchorMapping = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/anchor_mappings`;
      return useSWRMutation<AdminPatrolAnchorMapping, CustomError, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminPatrolAnchorMapping>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminPatrolAnchorMappings = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/systems/${systemId}/anchor_mappings`, query);
      return useSWR<Paginated<AdminPatrolAnchorMapping>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminPatrolAnchorMapping>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminPatrolAnchorMapping = useCallback(
    async (body: InsertAdminPatrolAnchorMappingBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/anchor_mappings`;
      const id = await api.post<{ id: string }, InsertAdminPatrolAnchorMappingBody>(url, body);
      mutateCache([url, `/admin/settings/patrol/systems/${body.systemId}/anchor_mappings`]);
      return id;
    },
    [api],
  );

  const updateAdminPatrolAnchorMapping = useCallback(
    async (patrolSystemId: string, body: UpdateAdminPatrolAnchorMappingBody): Promise<void> => {
      const url = `/admin/settings/patrol/anchor_mappings/${patrolSystemId}`;
      (await api.put<UpdateAdminPatrolAnchorMappingBody>(url, body),
        mutateCache([
          "/admin/settings/patrol/anchor_mappings",
          `/admin/settings/patrol/systems/${body.systemId}/anchor_mappings`,
        ]));
    },
    [api],
  );

  const deleteAdminPatrolAnchorMapping = useCallback(
    async (systemId: string, patrolAnchorMappingId: string): Promise<void> => {
      const url = `/admin/settings/patrol/anchor_mappings/${patrolAnchorMappingId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/patrol/anchor_mappings`,
        `/admin/settings/patrol/systems/${systemId}/anchor_mappings`,
      ]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (patrolAnchorMappingId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/anchor_mappings/${patrolAnchorMappingId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminPatrolAnchorMapping,
    useAdminPatrolAnchorMappings,
    insertAdminPatrolAnchorMapping,
    updateAdminPatrolAnchorMapping,
    deleteAdminPatrolAnchorMapping,
    generateUploadImageUrl,
  };
};
